/* eslint-disable @typescript-eslint/no-explicit-any,testing-library/await-async-queries */
import { walkWidgetPropsTree } from '@juvare/widget-ui/Widget';
import { walkWidgetConfigTree } from '../walkWidgetConfigTree';
import { getVisibleError, renderForm } from '../WidgetForm/Utils';
/**
 * Contains useful fields and function to work with the active form.
 * Reference to this class is passed in most widget events, for example: custom validation, on change event, button on click, etc.
 *
 * This class points to a single widget instance.
 * Use `select` / `up` / `upByType` / 'down` / 'downByType` functions to traverse the widget tree.
 */
export class WidgetHandler {
    /**
     * Creates a new handler for a widget instance.
     * @param {WidgetProps<any, any>} props - Runtime props of the current widget instance.
     * @param {WidgetDynamicRow} [dynamicRow] - When present, indicates this handler points at a child inside a dynamic row (e.g., DataGrid row).
     * @param {{ update?: boolean }} [status] - Internal mutable status object shared between handlers to request re-render.
     * @param {boolean} [status.update] - Flag indicating, whether status should update.
     * @returns {void}
     */
    constructor(props, dynamicRow, status) {
        this.props = props;
        this.dynamicRow = dynamicRow;
        this.status = status !== null && status !== void 0 ? status : {};
        this.formData = this.props.rootData;
        this.formParams = this.props.rootParams.formParams;
        this.config = this.props.config;
    }
    /**
     * Selected widget based on selector.
     *
     * Widget selector rules:
     * | Selector | Code                   | Notes                         |
     * |----------|------------------------|-------------------------------|
     * | `^`      | `handler.up()`         | Selects parent widget         |
     * | `/key`   | `handler.down('key')`  | Selects child widget by `key` |
     *
     * Examples:
     * | Selector         | Code                                           |
     * |------------------|------------------------------------------------|
     * | `^/key`          | `handler.up().down('key')`                     |
     * | `^^/key1/key2`   | `handler.up().up().down('path1').down('path2)` |
     *
     * @param {string} selector - Widget selector.
     * @returns {WidgetHandler | undefined} - Handler or undefined.
     */
    select(selector) {
        let handler;
        if (selector.startsWith('/')) {
            selector = selector.substring(1);
            handler = new WidgetHandler(this.props.rootWidgetProps, undefined, this.status);
        }
        else {
            handler = new WidgetHandler(this.props, this.dynamicRow, this.status);
        }
        const parts = selector.split(/([\^/])/);
        for (let i = 0; i < parts.length; ++i) {
            if (!handler) {
                return undefined;
            }
            const part = parts[i];
            if ((i === 0 && part === '') || part === '/') {
                // skip
            }
            else if (part === '^') {
                handler = handler.up();
                if (parts[i + 1] === '') {
                    ++i;
                }
            }
            else {
                handler = handler.down(part);
            }
        }
        return handler;
    }
    /**
     * Selects parent widget. If there is no parent same object is returned.
     * @returns {WidgetHandler} WidgetHandler.
     */
    up() {
        var _a;
        if (this.dynamicRow) {
            return new WidgetHandler(this.props, undefined, this.status);
        }
        else {
            if (this.props.parentProps) {
                return new WidgetHandler(this.props.parentProps, this.props.parentDynamicRow, this.status);
            }
            else {
                const parentModalWidgetHandler = (_a = this.props.rootParams.formParams) === null || _a === void 0 ? void 0 : _a.parentModalWidgetHandler;
                if (parentModalWidgetHandler) {
                    return parentModalWidgetHandler;
                }
                else {
                    return this;
                }
            }
        }
    }
    /**
     * Selects parent widget by recursively going up the tree trying to match widget by its type.
     *
     * @param {string} type - Widget type, for example `form.text`.
     * @returns {WidgetHandler | undefined} The parent widget handler if found, otherwise `undefined`.
     */
    upByType(type) {
        let handler = this.up();
        while (handler !== this && handler.config.type !== type) {
            handler = handler.up();
        }
        return handler !== this ? handler : undefined;
    }
    /**
     * Selects child widget by its key or index. Recursively checks children key is not set.
     *
     * @param {string} key - Widget config key.
     * @param {number} [index] - Optional index for list widgets (`form.dataGrid` / `form.modalPanelList` / `form.modalRowList`).
     * @returns {WidgetHandler | undefined} The child widget handler if found, otherwise `undefined`.
     */
    down(key, index) {
        if (this.dynamicRow) {
            for (const childProps of this.dynamicRow.childProps) {
                const found = findByKeyRecursive(childProps, key, index);
                if (found) {
                    return new WidgetHandler(found, undefined, this.status);
                }
            }
        }
        else if (this.props.childProps) {
            for (const childProps of this.props.childProps) {
                const found = findByKeyRecursive(childProps, key, index);
                if (found) {
                    return new WidgetHandler(found, undefined, this.status);
                }
            }
        }
        return undefined;
    }
    /**
     * Selects child widget by type.
     *
     * @param {string} type - Widget type, for example `form.text`.
     * @returns {WidgetHandler | undefined} WidgetHandler or undefined.
     */
    downByType(type) {
        var _a;
        let match;
        if (this.dynamicRow) {
            match = this.dynamicRow.childProps.find((p) => p.config.type === type);
        }
        else {
            match = (_a = this.props.childProps) === null || _a === void 0 ? void 0 : _a.find((p) => p.config.type === type);
        }
        return match ? new WidgetHandler(match, undefined, this.status) : undefined;
    }
    /**
     * Returns human-readable label and value pairs.
     * For example used in Handlebars templates when there is a need to render select label because values is a UUID.
     *
     * @param {string} [labelPrefix] - Optional prefix for the label.
     * @returns {{ label: string; text: string}} Label and text pairs.
     */
    getLabelValueAsTextPairs(labelPrefix) {
        if (this.props.widget.getLabelValueAsTextPairs) {
            return this.props.widget.getLabelValueAsTextPairs(this.props, labelPrefix);
        }
        else {
            return [];
        }
    }
    /**
     * Returns widgets data.
     *
     * @param {(value: any) => any} [mapper] - Optional mapper function for data to get.
     * @returns {any} Any.
     */
    getData(mapper) {
        if (this.dynamicRow) {
            const data = this.props.getDataArray()[this.dynamicRow.index];
            return mapper ? mapper(data) : data;
        }
        else {
            return this.props.getData(mapper);
        }
    }
    /**
     * @returns {boolean} Returns `true` if widget is in a row (children of `form.dataGrid` / `form.modalPanelList` / `form.modalRowList`).
     */
    isRow() {
        return this.dynamicRow != null;
    }
    /**
     * @returns {number | undefined} Returns row index if widget is in a row (children of `form.dataGrid` / `form.modalPanelList` / `form.modalRowList`).
     */
    getRowIndex() {
        if (this.dynamicRow) {
            return this.dynamicRow.index;
        }
        else {
            return undefined;
        }
    }
    /**
     * @param {any} value - Data to set.
     * @param {(value: any) => any} [mapper] - Optional mapper function for data to set.
     * @returns {void}
     */
    setData(value, mapper) {
        if (this.props.childDynamicRows) {
            while (this.props.childDynamicRows.length > 0) {
                this.props.deleteDataRow(0);
            }
            if (Array.isArray(value)) {
                for (let i = 0; i < value.length; ++i) {
                    this.props.insertDataRow(i);
                }
            }
        }
        this.props.setData(value, mapper);
        delete this.props.internalData.value;
        delete this.props.internalData.error;
        this.status.update = true;
    }
    /**
     * Hides the widget so that it is no longer visible. When widget is hidden its validation is disabled.
     *
     * When you call this function any custom configuration for widget visibility is removed.
     *
     * @param {hidden} hidden - Makes the widget hidden.
     */
    setHidden(hidden) {
        this.config.hidden = hidden;
        if (this.props.hidden !== hidden) {
            this.props.hidden = hidden;
            if (hidden && this.props.config.clearDataOnHide) {
                this.setData(undefined);
            }
            this.props.update();
            this.status.update = true;
        }
    }
    /**
     * Marks that this widget needs validation and update. Widget will be re-rendered with updated data and/or config.
     */
    update() {
        this.props.update();
        this.status.update = true;
    }
    /**
     * Renders the form. Use this only from asynchronous events.
     * Widget must be marked for rendering by calling one of these methods: `setData`, `setHidden`, `update`.
     *
     * @param {object} [options] - Rendering options.
     *  @param {boolean} [options.changed] - If `true`, the form will be in a changed state; navigating away will prompt to save changes.
     *  @param {boolean} [options.autoSaveDebounce] - If `true`, form will be automatically saved after some time.
     *  @param {boolean} [options.autoSaveImmediately] - If `true`, form will be automatically saved immediately.
     *  @returns {void} Void.
     */
    renderForm(options) {
        renderForm(this.props, options);
    }
    /**
     * Reloads list of possible values for widgets that have them (for example: `form.select`, `form.radio`, `form.checkboxList`).
     *
     * @param {boolean} clearData - Optional flag to clear widget data.
     * @returns {void} Void.
     */
    reloadValues(clearData) {
        if (clearData) {
            this.setData(undefined);
        }
        this.props.internalData.loadValues = true;
        this.status.update = true;
    }
    /**
     * Collects and returns visible error messages from this widget and its children.
     * Used by the `form.errors` widget.
     * @returns {{ id: string; message: string }[]} List of errors.
     */
    getVisibleErrorMessages() {
        const errors = [];
        walkWidgetPropsTree(this.props, (childProps) => {
            const message = getVisibleError(childProps);
            if (message) {
                errors.push({ id: childProps.id, message });
            }
        });
        return errors;
    }
    /**
     * @returns {boolean} Returns `true` if form is valid, `false` if any widget has error.
     */
    isFormValid() {
        return this.props.getRootErrorCount() === 0;
    }
    /**
     * Changes how errors are shown.
     * @param {'all' | 'changed' | 'none'} showErrors - Error category to show.
     * @returns {void}
     */
    setShowErrorsOverride(showErrors) {
        if (this.props.rootParams.showErrorsOverride !== showErrors) {
            this.props.rootParams.showErrorsOverride = showErrors;
            this.props.rootUpdateAll();
        }
    }
    /**
     * Disables widget. Optionally disables all children (recursive) too.
     *
     * @param {boolean} disabled - Disable or enable.
     * @param {boolean} [includeAllChildren] - If `true` disabled all children (recursive).
     */
    setDisabled(disabled, includeAllChildren) {
        if (includeAllChildren) {
            walkWidgetConfigTree(this.props.config, this.props.widgets, (widgetConfig) => (widgetConfig.disabled = disabled));
            this.props.rootUpdateAll();
        }
        else {
            this.props.config.disabled = disabled;
            this.update();
        }
    }
    /**
     * Sets required field in widget.
     *
     * @param {boolean} required - Required or not.
     */
    setRequired(required) {
        if (!this.props.config.validate) {
            this.props.config.validate = {};
        }
        this.props.config.validate.required = required;
        this.update();
    }
}
function findByKeyRecursive(props, key, index) {
    if (props.config.key === key) {
        return props;
    }
    else if (!props.config.key) {
        if (props.childProps) {
            for (const childProps of props.childProps) {
                const found = findByKeyRecursive(childProps, key, index);
                if (found) {
                    return found;
                }
            }
        }
        if (index != null && props.childDynamicRows && index >= 0 && index < props.childDynamicRows.length) {
            for (const childProps of props.childDynamicRows[index].childProps) {
                const found = findByKeyRecursive(childProps, key, index);
                if (found) {
                    return found;
                }
            }
        }
    }
    return undefined;
}
